/**
 * Hook pour gérer la queue d'actions du jeu
 * 
 * Ce hook gère:
 * - L'ajout d'actions à la queue
 * - L'exécution séquentielle des actions
 * - Les délais entre les actions (pour les animations)
 * - L'application des actions via le reducer
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { GameState, GameAction, createInitialGameState, serverMessageToActions, DELAYS } from './gameActions';
import { gameReducer, findTrickWinner } from './gameReducer';
import { GameHistoryRecorder, GameHistory } from './gameHistory';
import { log } from '../utils/logger';

function stripLeadingChienFollowUps(queue: GameAction[]): GameAction[] {
  const copy = [...queue];
  while (copy.length > 0 && (copy[0].type === 'delay' || copy[0].type === 'chien_vu')) {
    copy.shift();
  }
  return copy;
}

function hasChienFollowUp(queue: GameAction[]): boolean {
  return queue.some(a => a.type === 'chien_vu');
}

function appendChienFollowUps(queue: GameAction[]): GameAction[] {
  return [
    ...queue,
    { type: 'delay', ms: DELAYS.CHIEN_SHOWN },
    { type: 'chien_vu' },
  ];
}

// ============================================
// Helpers pour le suivi du tour et du temps limite
// ============================================

/**
 * Détermine si c'est au joueur local d'agir (pour le timer de temps limite)
 */
function isMyTurnToActForTimer(state: GameState): boolean {
  switch (state.phase) {
    case 2: return state.currentPlayer === state.myPosition;   // Enchères
    case 3: return state.currentPlayer === state.myPosition;   // Appel du roi
    case 5: return state.taker === state.myPosition;           // Écart (seul le preneur agit)
    case 6: return state.currentPlayer === state.myPosition;   // Jeu de la carte
    case 99999: return true;                                    // Score (le joueur doit cliquer "Prêt")
    default: return false;
  }
}

/**
 * Retourne le type de phase pour le timer (pour détecter les changements de phase)
 */
function getTimerPhaseType(state: GameState): string | null {
  switch (state.phase) {
    case 2: return 'contrat';
    case 3: return 'appel';
    case 5: return 'ecart';
    case 6: return 'carte';
    case 99999: return 'score';
    default: return null;
  }
}

/**
 * Applique une action via le reducer et met à jour le turnStartedAt si nécessaire.
 * Le timestamp receivedAt provient du moment où le message serveur a été reçu (avant mise en queue).
 */
function applyActionWithTurnTracking(
  currentState: GameState,
  action: GameAction,
  receivedAt?: number,
): GameState {
  const newState = gameReducer(currentState, action);

  let tracked = newState;
  if (
    tracked.dealTimeoutSeconds
    && tracked.dealTimeoutSeconds > 0
    && tracked.dealStartedAt === undefined
  ) {
    tracked = { ...tracked, dealStartedAt: receivedAt || Date.now() };
  }
  
  // Pas de time limits configurés → pas de tracking
  if (!tracked.timeLimits) return tracked;
  
  const prevPhaseType = getTimerPhaseType(currentState);
  const prevIsMyTurn = isMyTurnToActForTimer(currentState);
  const newPhaseType = getTimerPhaseType(tracked);
  const newIsMyTurn = isMyTurnToActForTimer(tracked);
  
  // Le tour vient de commencer, ou la phase a changé alors que c'est toujours mon tour
  if (newIsMyTurn && (!prevIsMyTurn || prevPhaseType !== newPhaseType)) {
    return { ...tracked, turnStartedAt: receivedAt || Date.now() };
  }
  
  // Le tour vient de se terminer → effacer le timestamp
  if (!newIsMyTurn && prevIsMyTurn) {
    return { ...tracked, turnStartedAt: undefined };
  }
  
  return tracked;
}

interface UseGameQueueOptions {
  tableId: string;
  myPseudo?: string; // Pour déterminer myPosition depuis les joueurs
  onStateChange?: (state: GameState) => void;
}

interface UseGameQueueReturn {
  // État du jeu
  gameState: GameState;
  
  // Queue
  queueLength: number;
  isProcessing: boolean;
  
  // Actions
  enqueueServerMessage: (message: { type: string; vals: any }) => void;
  enqueueAction: (action: GameAction) => void;
  enqueueActions: (actions: GameAction[]) => void;
  
  // Contrôles
  skipAnimations: () => void;
  /** Supprime délais / chien_vu en attente avant un nouvel ack chien */
  skipPendingChienAck: () => void;
  clearQueue: () => void;
  /** Réinitialise queue + état avant une resync complète (reprise différé rétro) */
  resetForFullSync: () => void;
  setPosition: (position: number) => void;
  
  // Replay
  getGameHistory: () => GameHistory | null;
  
  // Debug
  getCurrentQueue: () => GameAction[];
}

export function useGameQueue({ tableId, myPseudo, onStateChange }: UseGameQueueOptions): UseGameQueueReturn {
  // État du jeu
  const [gameState, setGameState] = useState<GameState>(() => createInitialGameState(tableId));
  
  // Déterminer myPosition quand les joueurs changent
  const myPseudoRef = useRef(myPseudo);
  myPseudoRef.current = myPseudo;
  
  useEffect(() => {
    if (!myPseudo || gameState.players.length === 0) return;
    
    // Chercher mon siège (position à la table), pas l'index dans le tableau players
    const myPlayer = gameState.players.find(
      p => p.name.toLowerCase() === myPseudo.toLowerCase()
    );
    const mySeat = myPlayer?.position;
    
    if (mySeat !== undefined && mySeat !== gameState.myPosition) {
      log.debug(`[GameQueue] Found my position: ${mySeat} (pseudo: ${myPseudo})`);
      setGameState(prev => ({
        ...prev,
        myPosition: mySeat,
      }));
      // Also record in history recorder so replay knows user's position
      historyRecorderRef.current.record({ type: 'set_position', position: mySeat } as GameAction);
    }
  }, [gameState.players, myPseudo, gameState.myPosition]);
  
  // Queue d'actions
  const queueRef = useRef<GameAction[]>([]);
  const [queueLength, setQueueLength] = useState(0);
  
  // État de traitement
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);
  const lastDataIdRef = useRef<number>(0);
  
  // Pour savoir si on attend la fin d'un pli (et qui est le gagnant)
  const pendingFinPliRef = useRef<number | null>(null);
  
  // Pour savoir si on attend l'affichage d'une enchère avant bid_advance
  const pendingBidAdvanceRef = useRef<{ action: GameAction; receivedAt?: number } | null>(null);
  
  // Référence à l'état pour le traitement async
  // IMPORTANT: On ne synchronise PAS stateRef depuis gameState via useEffect
  // car cela créerait une race condition avec le batching React.
  // stateRef est maintenu à jour manuellement avant chaque setGameState().
  const stateRef = useRef(gameState);
  
  // Enregistreur d'historique pour le replay
  const historyRecorderRef = useRef(new GameHistoryRecorder());
  
  // Callback pour notifier les changements d'état
  useEffect(() => {
    onStateChange?.(gameState);
  }, [gameState, onStateChange]);
  
  /**
   * Enregistre une action dans l'historique de replay.
   * Appelé pour chaque action traitée par le reducer.
   */
  const recordAction = useCallback((action: GameAction) => {
    const ts = (action as any)._receivedAt ?? Date.now();
    historyRecorderRef.current.record(action, ts);
  }, []);

  /**
   * Traite la prochaine action dans la queue
   */
  const processNextAction = useCallback(() => {
    const currentGeneration = generationRef.current;
    if (queueRef.current.length === 0) {
      processingRef.current = false;
      setIsProcessing(false);
      return;
    }
    
    processingRef.current = true;
    setIsProcessing(true);
    
    // Prendre la première action
    const action = queueRef.current.shift()!;
    setQueueLength(queueRef.current.length);
    
    log.debug(`[GameQueue] Processing action: ${action.type}, remaining: ${queueRef.current.length}`);
    
    // Ne pas avancer le jeu tant que le chien est affiché (clic ou timeout requis)
    const chienGateActive =
      stateRef.current.awaitingChienDismiss && stateRef.current.phase === 4;
    // ecart n'est pas bloqué ici : le reducer le diffère via ecartArrivedDuringChien
    const blockedDuringChien = chienGateActive && (
      action.type === 'carte' ||
      action.type === 'fin_pli' ||
      action.type === 'distribue'
    );
    const blockedBeforePlay = (action.type === 'carte' || action.type === 'fin_pli')
      && stateRef.current.phase < 6;
    if (blockedDuringChien || blockedBeforePlay) {
      log.debug(`[GameQueue] Deferring ${action.type} (chien=${chienGateActive}, phase=${stateRef.current.phase})`);
      queueRef.current.push(action);
      setQueueLength(queueRef.current.length);
      // Continuer la queue (ex. chien_vu derrière un ecart reçu trop tôt)
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (generationRef.current !== currentGeneration) {
          processingRef.current = false;
          setIsProcessing(false);
          return;
        }
        processNextAction();
      }, 0);
      return;
    }
    
    // Si c'est un délai, attendre puis continuer
    if (action.type === 'delay') {
      log.debug(`[GameQueue] Waiting ${action.ms}ms`);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (generationRef.current !== currentGeneration) {
          log.debug('[GameQueue] Skipping delayed action due to generation change');
          processingRef.current = false;
          setIsProcessing(false);
          return;
        }
        processNextAction();
      }, action.ms);
      return;
    }
    
    // Délai de réflexion avant enchère : skippé si l'enchère suivante est la nôtre
    if (action.type === 'bid_pre_delay') {
      const nextAction = queueRef.current[0];
      const nextBidder = nextAction?.type === 'contrat' ? Number((nextAction.data as any)?.joueur) : -1;
      if (nextBidder === stateRef.current.myPosition) {
        log.debug('[GameQueue] Skipping thinking delay for own bid');
        // Pas de délai de réflexion pour notre propre enchère
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (generationRef.current !== currentGeneration) return;
          processNextAction();
        }, 0);
        return;
      }
      log.debug(`[GameQueue] Bid thinking delay: ${action.ms}ms`);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (generationRef.current !== currentGeneration) {
          log.debug('[GameQueue] Skipping bid thinking delay due to generation change');
          return;
        }
        processNextAction();
      }, action.ms);
      return;
    }
    
    // Avancement après enchère : délai d'affichage puis transition
    // Le délai est supprimé/réduit si :
    //   - C'est notre propre enchère (pas besoin de lire notre propre bid)
    //   - Le prochain enchérisseur est l'utilisateur (pas de sens d'attendre avant de jouer)
    if (action.type === 'bid_advance') {
      const bidder = stateRef.current.currentPlayer;
      const isMyBid = bidder === stateRef.current.myPosition;
      const dealerHasSpoken = bidder === stateRef.current.dealer;
      const nextBidder = (bidder + 1) % stateRef.current.playerCount;
      const nextIsMe = !dealerHasSpoken && nextBidder === stateRef.current.myPosition;
      const displayDelay = (isMyBid || nextIsMe) ? 0 : DELAYS.BID_DISPLAY;
      const actionReceivedAt = (action as any)._receivedAt as number | undefined;
      
      log.debug(`[GameQueue] Bid advance: bidder=${bidder}, isMyBid=${isMyBid}, nextIsMe=${nextIsMe}, displayDelay=${displayDelay}ms`);
      
      // Mémoriser pour pouvoir traiter immédiatement si skipAnimations est appelé
      pendingBidAdvanceRef.current = { action, receivedAt: actionReceivedAt };
      
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        pendingBidAdvanceRef.current = null;
        if (generationRef.current !== currentGeneration) {
          log.debug('[GameQueue] Skipping bid_advance due to generation change');
          return;
        }
        
        // Appliquer bid_advance via le reducer (avance currentPlayer + phase)
        const newState = applyActionWithTurnTracking(stateRef.current, action, actionReceivedAt);
        stateRef.current = newState;
        setGameState(newState);
        recordAction(action);
        
        log.debug(`[GameQueue] After bid_advance: currentPlayer=${newState.currentPlayer}, phase=${newState.phase}`);
        
        // Continuer le traitement
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          if (generationRef.current !== currentGeneration) return;
          processNextAction();
        }, 0);
      }, displayDelay);
      return;
    }
    
    // AVANT de traiter une carte, vérifier si le pli précédent doit être terminé
    // On vérifie directement le nombre de cartes (plus fiable que le flag trickComplete)
    const currentState = stateRef.current;
    const trickIsFull = currentState.currentTrick.length >= currentState.playerCount;
    
    if (action.type === 'carte' && trickIsFull) {
      const trickWinner = findTrickWinner(currentState);
      log.debug(`[GameQueue] *** TRICK FULL (${currentState.currentTrick.length}/${currentState.playerCount}) *** Must end first. Winner: ${trickWinner}. Waiting ${DELAYS.TRICK_END}ms...`);
      
      // Remettre la carte dans la queue (au début)
      queueRef.current.unshift(action);
      setQueueLength(queueRef.current.length);
      
      // Marquer qu'on attend fin_pli
      pendingFinPliRef.current = trickWinner;
      
      // Capturer le timestamp de la carte qui a déclenché ce flow
      const carteReceivedAt = (action as any)._receivedAt as number | undefined;
      
      // Attendre puis terminer le pli
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (generationRef.current !== currentGeneration) {
          log.debug('[GameQueue] Skipping fin_pli (pre-card) due to generation change');
          return;
        }
        pendingFinPliRef.current = null;
        
        log.debug('[GameQueue] Processing fin_pli now (clearing previous trick)');
        const finPliAction = { type: 'fin_pli' as const, data: { gagnant: trickWinner } };
        const stateAfterFinPli = applyActionWithTurnTracking(
          stateRef.current,
          finPliAction,
          carteReceivedAt,
        );
        stateRef.current = stateAfterFinPli;
        setGameState(stateAfterFinPli);
        recordAction(finPliAction);
        
        log.debug(`[GameQueue] After fin_pli: numPli=${stateAfterFinPli.numPli}, trick=${stateAfterFinPli.currentTrick.length}`);
        
        // Maintenant traiter la carte qu'on avait remise dans la queue
        processNextAction();
      }, DELAYS.TRICK_END);
      
      return;
    }
    
    // Appliquer l'action via le reducer (avec suivi du tour pour le timer)
    const actionReceivedAt = (action as any)._receivedAt as number | undefined;
    const newState = applyActionWithTurnTracking(currentState, action, actionReceivedAt);
    stateRef.current = newState;
    setGameState(newState);
    
    // Enregistrer l'action dans l'historique de replay
    recordAction(action);

    // 5 joueurs : synchroniser le timer chien avec la révélation du roi appelé
    if (action.type === 'chien') {
      if (newState.pendingChienReveal && newState.kingCallRevealPending) {
        queueRef.current = stripLeadingChienFollowUps(queueRef.current);
        setQueueLength(queueRef.current.length);
        log.debug('[GameQueue] chien held for king reveal — deferred auto-dismiss');
      } else if (newState.awaitingChienDismiss && !hasChienFollowUp(queueRef.current)) {
        queueRef.current = appendChienFollowUps(queueRef.current);
        setQueueLength(queueRef.current.length);
        log.debug('[GameQueue] chien shown — ensured auto-dismiss follow-ups');
      }
    } else if (
      action.type === 'king_call_reveal_done' &&
      newState.awaitingChienDismiss &&
      !hasChienFollowUp(queueRef.current)
    ) {
      queueRef.current = appendChienFollowUps(queueRef.current);
      setQueueLength(queueRef.current.length);
      log.debug('[GameQueue] king reveal done — ensured chien auto-dismiss follow-ups');
    }
    
    // Détecter si le pli vient de se compléter (après avoir traité la dernière carte)
    const newTrickIsFull = newState.currentTrick.length >= newState.playerCount;
    
    if (newTrickIsFull) {
      const trickWinner = findTrickWinner(newState);
      log.debug(`[GameQueue] *** TRICK NOW FULL (${newState.currentTrick.length}/${newState.playerCount}) *** Processing fin_pli after delay. Winner: ${trickWinner}`);
      
      // Marquer qu'on attend fin_pli
      pendingFinPliRef.current = trickWinner;
      
      // Toujours traiter fin_pli après un délai (pour voir le pli complet)
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (generationRef.current !== currentGeneration) {
          log.debug('[GameQueue] Skipping fin_pli (post-card) due to generation change');
          return;
        }
        pendingFinPliRef.current = null;
        
        log.debug('[GameQueue] Processing fin_pli now');
        const finPliAction2 = { type: 'fin_pli' as const, data: { gagnant: trickWinner } };
        const stateAfterFinPli = applyActionWithTurnTracking(
          stateRef.current,
          finPliAction2,
          actionReceivedAt,  // Timestamp de la carte qui a complété le pli
        );
        stateRef.current = stateAfterFinPli;
        setGameState(stateAfterFinPli);
        recordAction(finPliAction2);
        
        log.debug(`[GameQueue] After fin_pli: numPli=${stateAfterFinPli.numPli}, trick=${stateAfterFinPli.currentTrick.length}`);
        
        // Continuer à traiter la queue
        processNextAction();
      }, DELAYS.TRICK_END);
      return;
    }
    
    // Traiter immédiatement la prochaine action (pas de délai entre les actions non-delay)
    // Utiliser setTimeout(0) pour permettre à React de faire le rendu
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (generationRef.current !== currentGeneration) {
        log.debug('[GameQueue] Skipping next action due to generation change');
        return;
      }
      processNextAction();
    }, 0);
  }, [recordAction]);
  
  /**
   * Démarre le traitement si nécessaire
   */
  const startProcessing = useCallback(() => {
    if (!processingRef.current && queueRef.current.length > 0) {
      processNextAction();
    }
  }, [processNextAction]);
  
  /**
   * Ajoute une action à la queue
   */
  const enqueueAction = useCallback((action: GameAction) => {
    log.debug(`[GameQueue] Enqueueing action: ${action.type}`);
    queueRef.current.push(action);
    setQueueLength(queueRef.current.length);
    startProcessing();
  }, [startProcessing]);
  
  /**
   * Ajoute plusieurs actions à la queue
   */
  const enqueueActions = useCallback((actions: GameAction[]) => {
    if (actions.length === 0) return;
    log.debug(`[GameQueue] Enqueueing ${actions.length} actions`);
    queueRef.current.push(...actions);
    setQueueLength(queueRef.current.length);
    startProcessing();
  }, [startProcessing]);

  /**
   * Réinitialise la queue (utile quand une nouvelle donne arrive)
   */
  const resetQueueForNewDeal = useCallback((reason: string) => {
    log.debug(`[GameQueue] Resetting queue for new deal: ${reason}`);
    generationRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    queueRef.current = [];
    setQueueLength(0);
    processingRef.current = false;
    setIsProcessing(false);
  }, []);

  /**
   * Réinitialise l'état pour une nouvelle donne tout en conservant les joueurs
   */
  const resetStateForNewDeal = useCallback((incomingIdPartie?: string) => {
    const baseState = createInitialGameState(tableId);
    const preservedPlayers = stateRef.current.players;
    const preservedPlayerCount = stateRef.current.playerCount;
    const preservedMyPosition = stateRef.current.myPosition;
    const preservedTimeLimits = stateRef.current.timeLimits;
    const preservedHasOtherHumans = stateRef.current.hasOtherHumans;

    const newState: GameState = {
      ...baseState,
      idPartie: incomingIdPartie ?? baseState.idPartie,
      players: preservedPlayers,
      playerCount: preservedPlayerCount,
      myPosition: preservedMyPosition,
      timeLimits: preservedTimeLimits,         // Conserver les temps limites entre les donnes
      hasOtherHumans: preservedHasOtherHumans, // Conserver l'info sur les humains
      turnStartedAt: undefined,                // Réinitialiser le timer de tour
    };

    log.debug('[GameQueue] Resetting state for new deal', {
      incomingIdPartie,
      preservedPlayers: preservedPlayers.length,
      preservedMyPosition,
      hasTimeLimits: !!preservedTimeLimits,
    });

    stateRef.current = newState;
    setGameState(newState);
  }, [tableId]);
  
  /**
   * Convertit un message serveur en actions et les ajoute à la queue
   */
  const enqueueServerMessage = useCallback((message: { type: string; vals: any }) => {
    log.debug(`[GameQueue] Received server message: ${message.type}`);
    const incomingIdPartie = message?.vals?.idPartie !== undefined ? String(message.vals.idPartie) : undefined;
    const dataId = message?.vals?.dataId !== undefined ? String(message.vals.dataId) : undefined;
    const dataIdNumber = dataId ? Number(dataId) : undefined;
    log.debug('[GameQueue] Message meta', {
      type: message.type,
      dataId,
      lastDataId: lastDataIdRef.current,
      incomingIdPartie,
      currentIdPartie: stateRef.current.idPartie,
      queueLength: queueRef.current.length,
      isProcessing: processingRef.current,
      phase: stateRef.current.phase,
    });

    if (dataIdNumber !== undefined && !Number.isNaN(dataIdNumber)) {
      if (dataIdNumber <= lastDataIdRef.current) {
        log.debug('[GameQueue] Ignoring message with old dataId', { dataIdNumber });
        return;
      }
      lastDataIdRef.current = dataIdNumber;
    }
    if (
      (message.type === 'distribue' || message.type === 'distribue_cartes') &&
      incomingIdPartie &&
      incomingIdPartie !== stateRef.current.idPartie
    ) {
      resetQueueForNewDeal(`idPartie ${stateRef.current.idPartie ?? 'none'} -> ${incomingIdPartie}`);
      lastDataIdRef.current = dataIdNumber ?? lastDataIdRef.current;
      resetStateForNewDeal(incomingIdPartie);
    }
    let actions = serverMessageToActions(message);
    
    // Horodater chaque action avec le moment de réception du message serveur.
    // Ce timestamp sert de référence pour le décompte de temps limite :
    // le timer commence à compter dès que le serveur a transmis les données,
    // même si l'affichage n'est pas encore à jour (queue d'animations).
    const receivedAt = Date.now();
    for (const action of actions) {
      (action as any)._receivedAt = receivedAt;
    }
    
    if (actions.length > 0) {
      enqueueActions(actions);
    }
  }, [enqueueActions, resetQueueForNewDeal, resetStateForNewDeal]);
  
  const skipPendingChienAck = useCallback(() => {
    log.debug('[GameQueue] skipPendingChienAck: clearing chien delays from queue');
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    queueRef.current = queueRef.current.filter(
      a => a.type !== 'delay' && a.type !== 'chien_vu',
    );
    setQueueLength(queueRef.current.length);
    pendingFinPliRef.current = null;
    pendingBidAdvanceRef.current = null;

    // Toujours débloquer : un timer expiré ou une action différée peut laisser processingRef à true
    processingRef.current = false;
    setIsProcessing(false);
    if (queueRef.current.length > 0) {
      processNextAction();
    }
  }, [processNextAction]);

  /**
   * Accélère l'animation en cours (annule le délai et passe à l'action suivante)
   * Si on attendait la fin d'un pli, traite le fin_pli immédiatement
   */
  const skipAnimations = useCallback(() => {
    log.debug('[GameQueue] *** SKIP REQUESTED ***');
    log.debug(`[GameQueue] timerRef: ${timerRef.current ? 'active' : 'null'}, pendingFinPli: ${pendingFinPliRef.current}`);
    log.debug(`[GameQueue] Current trick: ${stateRef.current.currentTrick.length}/${stateRef.current.playerCount}, queue length: ${queueRef.current.length}`);
    
    // Si un timer est en cours, l'annuler
    if (timerRef.current) {
      log.debug('[GameQueue] Cancelling current timer');
      clearTimeout(timerRef.current);
      timerRef.current = null;
      
      // Si on attendait fin_pli, le traiter maintenant
      if (pendingFinPliRef.current !== null) {
        const trickWinner = pendingFinPliRef.current;
        pendingFinPliRef.current = null;
        
        log.debug(`[GameQueue] Processing pending fin_pli, winner: ${trickWinner}`);
        const skipFinPliAction = { type: 'fin_pli' as const, data: { gagnant: trickWinner } };
        const stateAfterFinPli = applyActionWithTurnTracking(
          stateRef.current,
          skipFinPliAction,
        );
        stateRef.current = stateAfterFinPli;
        setGameState(stateAfterFinPli);
        recordAction(skipFinPliAction);
        
        log.debug(`[GameQueue] After fin_pli: numPli=${stateAfterFinPli.numPli}, trick cleared: ${stateAfterFinPli.currentTrick.length === 0}`);
      }
      
      // Si on attendait l'affichage d'une enchère (bid_advance), le traiter maintenant
      if (pendingBidAdvanceRef.current !== null) {
        const { action: bidAction, receivedAt } = pendingBidAdvanceRef.current;
        pendingBidAdvanceRef.current = null;
        
        log.debug('[GameQueue] Processing pending bid_advance');
        const stateAfterAdvance = applyActionWithTurnTracking(
          stateRef.current,
          bidAction,
          receivedAt,
        );
        stateRef.current = stateAfterAdvance;
        setGameState(stateAfterAdvance);
        recordAction(bidAction);
      }
      
      // Reprendre le traitement immédiatement
      log.debug('[GameQueue] Resuming queue processing...');
      processNextAction();
    } else {
      log.debug('[GameQueue] No timer to skip, nothing to do');
    }
  }, [processNextAction, recordAction]);
  
  /**
   * Vide la queue
   */
  const clearQueue = useCallback(() => {
    log.debug('[GameQueue] Clearing queue');
    
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    queueRef.current = [];
    setQueueLength(0);
    processingRef.current = false;
    setIsProcessing(false);
  }, []);

  const resetForFullSync = useCallback(() => {
    resetQueueForNewDeal('full sync');
    resetStateForNewDeal(stateRef.current.idPartie);
  }, [resetQueueForNewDeal, resetStateForNewDeal]);
  
  /**
   * Définit la position du joueur
   */
  const setPosition = useCallback((position: number) => {
    enqueueAction({ type: 'set_position', position });
  }, [enqueueAction]);
  
  /**
   * Construit et retourne l'historique complet de la partie pour le replay.
   * Retourne null si pas assez de données enregistrées.
   */
  const getGameHistory = useCallback((): GameHistory | null => {
    // Pass current players as fallback in case joueurs/infos_table wasn't recorded
    const fallbackPlayers = stateRef.current.players
      .filter(p => p.name)
      .map(p => ({ pseudo: p.name, position: p.position }));
    const history = historyRecorderRef.current.buildHistory(fallbackPlayers);
    // Ensure myPosition is correct from current state as fallback
    if (history && history.myPosition === 0 && stateRef.current.myPosition !== 0) {
      history.myPosition = stateRef.current.myPosition;
    }
    return history;
  }, []);

  /**
   * Retourne la queue actuelle (pour debug)
   */
  const getCurrentQueue = useCallback(() => {
    return [...queueRef.current];
  }, []);
  
  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  return {
    gameState,
    queueLength,
    isProcessing,
    enqueueServerMessage,
    enqueueAction,
    enqueueActions,
    skipAnimations,
    skipPendingChienAck,
    clearQueue,
    resetForFullSync,
    setPosition,
    getGameHistory,
    getCurrentQueue,
  };
}
